import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {Box, Center, Container, Spinner, useDisclosure, useToast} from '@chakra-ui/react';
import {useParams, useSearchParams, useNavigate} from 'react-router-dom';
import {TextG14, TextG16} from '../../components/typography';
import ProfileIdentity from '../../components/Profile/ProfileIdentity';
import {usePublicProfile} from '../../hooks/usePublicProfile';
import {useUserPlans, useSharedPlans, UserEvent} from '../../hooks/useUserEvents';
import {useSendFriendRequest, useCancelFriendRequest, useUnfriend} from '../../hooks/useFriendRequest';
import useAuth from '../../hooks/useAuth';
import {useAvailability} from '../../hooks/useAvailability';
import NeighborProfileHeader from './components/NeighborProfileHeader';
import MutualConnections from './components/MutualConnections';
import ConnectMessageButtons from './components/ConnectMessageButtons';
import ConversationStarterCards from './components/ConversationStarterCards';
import InSyncCard from './components/InSyncCard';
import AvailabilityChips from './components/AvailabilityChips';
import MessageModal from './components/MessageModal';
import ReportBlockMenu from './components/ReportBlockMenu';
import TheirPlans from './components/TheirPlans';
import ArticleModal from '../../components/Articles/Modal';
import PromptModal from '../../components/Modals/PromptModal';
import {ConnectionStatus} from '../../hooks/usePublicProfile';
import {getDisplayName} from '../../utils/nameFormatters';
import {DAY_ABBREV} from '../../utils/availability';

type ActiveTab = 'profile' | 'plans';

function NeighborProfile() {
  const {id} = useParams<{id: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {user: authUser} = useAuth();
  const toast = useToast();
  const {data: profile, isLoading, isError} = usePublicProfile(id);
  const {data: myAvailability} = useAvailability();
  const sendFriendRequest = useSendFriendRequest(id);
  const cancelFriendRequestMutation = useCancelFriendRequest(id);
  const unfriendMutation = useUnfriend(id);

  const [activeTab, setActiveTab] = useState<ActiveTab>('profile');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showUnfriendPrompt, setShowUnfriendPrompt] = useState(false);
  const [localConnectionStatus, setLocalConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [sharedOnly, setSharedOnly] = useState(false);

  const needsPlans = activeTab === 'plans' || showMessageModal;
  const plansQuery = useUserPlans(id, needsPlans);
  // Shared plans are needed whenever the message modal is open (messaging is
  // only available through shared event chats) or when the user has toggled
  // the "shared only" filter on the Plans tab.
  const sharedPlansQuery = useSharedPlans(id, showMessageModal || (sharedOnly && needsPlans));

  // Article modal
  const {isOpen: isArticleModalOpen, onOpen: onArticleModalOpen, onClose: onArticleModalClose} = useDisclosure();
  const [articleModalType, setArticleModalType] = useState<string>('event');

  const handleOpenArticle = useCallback((plan: UserEvent) => {
    const type = plan.is_meetup ? 'meet-up' : plan.type === 'event' ? 'event' : plan.type;
    setArticleModalType(type);
    searchParams.set('id', plan.id.toString());
    searchParams.set('articleType', type);
    setSearchParams(searchParams, {replace: true});
    onArticleModalOpen();
  }, [searchParams, setSearchParams, onArticleModalOpen]);

  const handleCloseArticle = useCallback(() => {
    searchParams.delete('id');
    searchParams.delete('articleType');
    setSearchParams(searchParams, {replace: true});
    onArticleModalClose();
  }, [searchParams, setSearchParams, onArticleModalClose]);

  // Auto-connect when arriving via QR code (?action=connect)
  const autoConnectAttempted = useRef(false);
  useEffect(() => {
    if (
      autoConnectAttempted.current ||
      !profile ||
      isLoading ||
      searchParams.get('action') !== 'connect'
    ) return;
    autoConnectAttempted.current = true;

    const status = localConnectionStatus || profile.connectionStatus || 'none';
    if (status !== 'none' || String(profile.id) === String(authUser?.id)) return;

    sendFriendRequest.mutateAsync(profile.id).then(() => {
      setLocalConnectionStatus('pending');
      toast({title: 'Friend request sent!', status: 'success', duration: 3000});
    }).catch(() => {
      // mutation handles error
    });
  }, [profile, isLoading]);

  const connectionStatus = localConnectionStatus || profile?.connectionStatus || 'none';

  const handleConnect = async () => {
    if (!profile) return;
    try {
      await sendFriendRequest.mutateAsync(profile.id);
      setLocalConnectionStatus('pending');
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelRequest = async () => {
    if (!profile?.friendRequestId) return;
    try {
      await cancelFriendRequestMutation.mutateAsync(profile.friendRequestId);
      setLocalConnectionStatus('none');
    } catch {
      // Error handled by mutation
    }
  };

  const handleUnfriend = async () => {
    if (!profile) return;
    try {
      await unfriendMutation.mutateAsync(profile.id);
      setLocalConnectionStatus('none');
    } catch {
      // Error handled by mutation
    } finally {
      setShowUnfriendPrompt(false);
    }
  };

  // Compute shared availability between auth user and this profile
  const sharedTimes = useMemo(() => {
    const mine = myAvailability;
    const theirs = profile?.availability;
    if (!mine || !theirs) return [];

    const TIME_LABEL: Record<string, string> = {
      Morning: 'mornings', Afternoon: 'afternoons', Evening: 'evenings',
    };
    const TIME_EMOJI: Record<string, string> = {
      Morning: '🌅', Afternoon: '✨', Evening: '🏙️',
    };

    // Check general overlap (e.g. both selected "Morning")
    const generalOverlap = (mine.general || []).filter(
      (slot) => (theirs.general || []).includes(slot),
    );
    const results: {label: string; emoji: string}[] = generalOverlap.map(
      (slot) => ({label: TIME_LABEL[slot] || slot, emoji: TIME_EMOJI[slot] || '✨'}),
    );

    // Check specific day overlap
    const mySpecific = mine.specific || {};
    const theirSpecific = theirs.specific || {};
    // Group overlapping days by shared time slot for compact display
    const slotToDays: Record<string, string[]> = {};
    for (const day of Object.keys(mySpecific)) {
      if (!theirSpecific[day]) continue;
      const overlap = mySpecific[day].filter((t) => theirSpecific[day].includes(t));
      for (const slot of overlap) {
        const key = TIME_LABEL[slot] || slot;
        if (!slotToDays[key]) slotToDays[key] = [];
        slotToDays[key].push(DAY_ABBREV[day] || day);
      }
    }
    for (const [timeLabel, days] of Object.entries(slotToDays)) {
      const emoji = Object.entries(TIME_LABEL).find(([, v]) => v === timeLabel)?.[0];
      results.push({label: `${days.join(' & ')} ${timeLabel}`, emoji: TIME_EMOJI[emoji || ''] || '✨'});
    }

    return results;
  }, [myAvailability, profile?.availability]);

  const sharedEvents = useMemo(
    () => sharedPlansQuery.plans.filter((e) => e.type === 'event').map((e) => ({id: e.id, title: e.title})),
    [sharedPlansQuery.plans],
  );

  if (isLoading || (!profile && !isError)) {
    return (
      <Container p={0} maxW="100%" bg="white" minH="100vh">
        <NeighborProfileHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onMenuOpen={() => {}}
        />
        <Center py={20}>
          <Spinner size="lg" color="#E02F84" />
        </Center>
      </Container>
    );
  }

  if (isError || !profile) {
    return (
      <Container p={0} maxW="100%" bg="white" minH="100vh">
        <NeighborProfileHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onMenuOpen={() => {}}
        />
        <Center flexDirection="column" py={20} px={6} textAlign="center">
          <Box
            w="56px"
            h="56px"
            borderRadius="full"
            bg="#FAFAFA"
            border="1px solid #EBEBEB"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={4}>
            <i className="fi fi-rr-user-slash" aria-hidden="true" style={{fontSize: '22px', color: '#757D84'}} />
          </Box>
          <TextG16 mt="0" fontWeight="700" mb={2}>
            Profile unavailable
          </TextG16>
          <TextG14 mt="0" color="#757D84" mb={5} maxW="280px">
            We couldn’t load this profile. It may have been removed or you may not have access.
          </TextG14>
          <Box
            as="button"
            onClick={() => navigate(-1)}
            bg="#1A1A1A"
            color="white"
            px={5}
            py={3}
            borderRadius="30px">
            <TextG14 mt="0" fontWeight="700" color="white">
              Go back
            </TextG14>
          </Box>
        </Center>
      </Container>
    );
  }

  // First name only on user-facing surfaces (privacy / Walkabout's real-world
  // connection focus). Use `getFullName` only where a legal/full name is
  // genuinely required (own profile, tickets, contact form pre-fill).
  const displayName = getDisplayName(profile);

  return (
    <Container p={0} maxW="100%" bg="white" minH="100vh">
      <NeighborProfileHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onMenuOpen={() => setShowMenu(true)}
      />

      <ReportBlockMenu
        userId={profile.id}
        userName={displayName}
        isOpen={showMenu}
        onClose={() => setShowMenu(false)}
      />

      {/* Avatar + Name + Badges */}
      <ProfileIdentity
        name={displayName}
        handle={profile.handle}
        photoUrl={profile.profilePhotoUrl}
        neighborhood={profile.neighborhood}
        city={profile.city}
        hasActiveMembership={profile.hasActiveMembership}
        isCloseNeighbor={profile.isCloseNeighbor}
      />

      {activeTab === 'profile' ? (
        <Box id="neighbor-profile-panel" role="tabpanel" pb={20}>
          {/* Introduction */}
          {profile.aboutMe && (
            <Box px={4} mb={4} mt={2}>
              <TextG16 mt="0" color="#4D4D4D" lineHeight="20px" mb={2}>
                I could happily nerd out about...
              </TextG16>
              <TextG16 mt="0" fontWeight="700" lineHeight="22px" fontSize="15px">
                {profile.aboutMe}
              </TextG16>
            </Box>
          )}

          {/* Mutual connections */}
          <MutualConnections
            connections={profile.mutualConnections}
            totalCount={profile.mutualConnectionCount}
          />

          {/* Connect + Message buttons */}
          <ConnectMessageButtons
            connectionStatus={connectionStatus}
            onConnect={handleConnect}
            onCancelRequest={handleCancelRequest}
            onUnfriend={() => setShowUnfriendPrompt(true)}
            onMessage={() => setShowMessageModal(true)}
            isConnecting={sendFriendRequest.isLoading}
          />

          {/* Conversation Starters */}
          <ConversationStarterCards
            starters={profile.conversationStarters}
            onMessage={() => setShowMessageModal(true)}
          />

          {/* In Sync card */}
          <InSyncCard
            profileName={displayName}
            profilePhotoUrl={profile.profilePhotoUrl}
            sharedTimes={sharedTimes}
            sharedEvents={sharedEvents}
          />

          {/* Availability */}
          <AvailabilityChips availability={profile.availability} />
        </Box>
      ) : (
        <Box id="neighbor-plans-panel" role="tabpanel">
          <TheirPlans profile={profile} plansQuery={plansQuery} sharedPlansQuery={sharedPlansQuery} sharedOnly={sharedOnly} setSharedOnly={setSharedOnly} onOpenArticle={handleOpenArticle} />
        </Box>
      )}

      {/* Article Modal (event/place detail view) */}
      {searchParams.has('id') && (
        <ArticleModal
          open={isArticleModalOpen}
          close={handleCloseArticle}
          type={articleModalType}
        />
      )}

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        userId={profile.id}
        handle={profile.handle}
        events={sharedPlansQuery.plans.filter((p) => p.type === 'event')}
        isLoading={sharedPlansQuery.isLoading}
      />

      {/* Unfriend confirmation */}
      <PromptModal
        isOpen={showUnfriendPrompt}
        onClose={() => setShowUnfriendPrompt(false)}
        title={`Remove ${profile.name} as a connection?`}
        description="They won't be notified, but you'll no longer appear as connections."
        declineText="Cancel"
        declineCallback={() => setShowUnfriendPrompt(false)}
        acceptText="Remove"
        acceptCallback={handleUnfriend}
      />
    </Container>
  );
}

export default NeighborProfile;
